import { Tooltip } from "@/components/ui/Tooltip";

export type FocalPosition =
  | "top-left"
  | "top-center"
  | "top-right"
  | "center-left"
  | "center"
  | "center-right"
  | "bottom-left"
  | "bottom-center"
  | "bottom-right";

export interface PositionGridSelectorProps {
  value: string;
  onChange: (pos: string) => void;
  disabled?: boolean;
  className?: string;
  ariaLabel?: string;
}

export function PositionGridSelector({
  value,
  onChange,
  disabled = false,
  className = "",
  ariaLabel = "Focal position anchor matrix",
}: PositionGridSelectorProps) {
  // Normalize legacy values ("top" -> "top-center", "bottom" -> "bottom-center")
  const normalize = (val: string): string => {
    const v = val.toLowerCase().trim();
    if (v === "top") return "top-center";
    if (v === "bottom") return "bottom-center";
    if (v === "left") return "center-left";
    if (v === "right") return "center-right";
    return v || "center";
  };

  const normalizedValue = normalize(value);

  const gridCells: {
    id: string;
    label: string;
    targetValue: FocalPosition;
  }[] = [
    { id: "tl", label: "Top Left", targetValue: "top-left" },
    { id: "tc", label: "Top Center", targetValue: "top-center" },
    { id: "tr", label: "Top Right", targetValue: "top-right" },
    { id: "ml", label: "Center Left", targetValue: "center-left" },
    { id: "mc", label: "Center", targetValue: "center" },
    { id: "mr", label: "Center Right", targetValue: "center-right" },
    { id: "bl", label: "Bottom Left", targetValue: "bottom-left" },
    { id: "bc", label: "Bottom Center", targetValue: "bottom-center" },
    { id: "br", label: "Bottom Right", targetValue: "bottom-right" },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`inline-grid grid-cols-3 gap-1.5 p-2 rounded-xl border border-border bg-muted/30 select-none ${className}`}
    >
      {gridCells.map((cell) => {
        const isSelected = normalizedValue === cell.targetValue;

        return (
          <Tooltip key={cell.id} content={cell.label} side="top">
            <button
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={disabled}
              onClick={() => !disabled && onChange(cell.targetValue)}
              className={`h-9 w-9 rounded-lg border transition-all flex items-center justify-center outline-none cursor-pointer focus-visible:ring-2 focus-visible:ring-primary ${
                isSelected
                  ? "border-primary bg-primary text-primary-foreground shadow-xs ring-1 ring-primary"
                  : "border-border/80 bg-card hover:border-primary/50 text-muted-foreground hover:text-foreground"
              } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <div
                className={`h-2.5 w-2.5 rounded-full transition-all ${
                  isSelected
                    ? "bg-primary-foreground scale-125"
                    : "bg-muted-foreground/40 hover:bg-muted-foreground/70"
                }`}
              />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}
